/**
 * One endpoint, one 44px line.
 *
 * The row is a CSS grid rather than a `<tr>` because the body is virtualized:
 * only ~20 of the 1,062 rows exist in the DOM at a time, and a `<tbody>` with
 * absolutely-positioned children is invalid markup that browsers reflow badly.
 * The grid template lives in `ENDPOINT_GRID` and is shared verbatim with the
 * header row in `EndpointTable`, so the two can never drift apart.
 *
 * ARIA does the job the table element would have done: the scroll container is
 * a `grid`, this is a `row`, and each cell is a `gridcell`.
 */
import type { CSSProperties } from 'react'

import { useI18n } from '../i18n'
import type { EndpointSummary, WindowId } from '../api/types'
import { HealthBadge } from './HealthBadge'
import { StatusRatioBar } from './StatusRatioBar'
import {
  BUCKET_STYLES,
  COLUMN_BUCKET,
  COLUMN_DESCRIPTION_KEYS,
  STATUS_COLUMNS,
  columnFor,
  numberFormat,
} from './statusColors'

/** Fixed row height, in px. The virtualizer measures nothing — every row is
 *  this tall — which is what keeps scrolling 1,062 rows smooth. */
export const ROW_HEIGHT = 44

/**
 * The single source of truth for the table's column geometry.
 *
 * Written out as one literal string because Tailwind scans source text: a
 * template built from variables would compile to no CSS at all.
 */
export const ENDPOINT_GRID =
  'grid grid-cols-[7rem_minmax(18rem,1fr)_6rem_repeat(9,4rem)_8rem_7rem] items-center gap-x-3 px-3'

/**
 * Windows for which `traffic_cliff` carries information.
 *
 * The backend computes the cliff against a 24h baseline, and every window is
 * nested inside the next, so at 24h the "short" window and the baseline are
 * literally the same data and at 72h/7d the short window *contains* the
 * baseline. `traffic_cliff` is therefore structurally `False` on those three
 * windows — see the note in `app/routes/metrics.py`. Rendering "no cliff" there
 * would be reporting an answer to a question that was never asked, so the
 * column says outright that the comparison does not apply.
 */
export const CLIFF_WINDOWS: ReadonlySet<WindowId> = new Set<WindowId>(['30m', '6h', '12h'])

export interface EndpointRowProps {
  endpoint: EndpointSummary
  window: WindowId
  /** Wired to the Task 23 drill-down. Without it the row is not interactive. */
  onSelect?: (endpointId: string) => void
  /** Absolute positioning from the virtualizer. */
  style?: CSSProperties
  /** This row's detail panel is open. With the panel beside the table rather
   *  than above it, this marker is the only thing tying the two together. */
  selected?: boolean
}

/** Bucket totals as a lookup, so a bucket with no traffic can render as "—"
 *  rather than as a 0 we never measured. */
function bucketMap(byStatus: Record<string, number>): Partial<Record<string, number>> {
  const out: Record<string, number> = {}
  for (const [code, n] of Object.entries(byStatus)) {
    const status = Number(code)
    if (!Number.isFinite(status) || n <= 0) continue
    const bucket = columnFor(status)
    // Codes with no column of their own (202, 401, 403, 404, 405, 3xx) are
    // dropped here on purpose. They are still counted in the Mix bar, which
    // classifies by colour bucket and covers every request, and broken out
    // individually in the drill-down pie.
    if (bucket === null) continue
    out[bucket] = (out[bucket] ?? 0) + n
  }
  return out
}

export function EndpointRow({
  endpoint,
  window,
  onSelect,
  style,
  selected = false,
}: EndpointRowProps) {
  const { t } = useI18n()
  const buckets = bucketMap(endpoint.by_status)
  const cliffApplies = CLIFF_WINDOWS.has(window)
  /** `total === null` is "we have no data", which is never a statement about
   *  what the endpoint did — only about what we looked at. */
  const measured = endpoint.total !== null

  return (
    <div
      role="row"
      data-testid="endpoint-row"
      data-endpoint-id={endpoint.endpoint_id}
      data-health={endpoint.health}
      data-selected={selected || undefined}
      aria-selected={selected || undefined}
      style={{ height: ROW_HEIGHT, ...style }}
      className={`${ENDPOINT_GRID} border-b border-border/60 text-sm hover:bg-surface ${
        selected ? 'bg-accent/10 ring-1 ring-inset ring-accent/40' : ''
      }`}
    >
      <span role="gridcell" data-testid="cell-health">
        <HealthBadge state={endpoint.health} />
      </span>

      <span role="gridcell" className="min-w-0" data-testid="cell-endpoint">
        {onSelect ? (
          <button
            type="button"
            onClick={() => onSelect(endpoint.endpoint_id)}
            title={`${endpoint.method} ${endpoint.path}`}
            className="flex w-full min-w-0 items-baseline gap-2 text-left hover:underline"
          >
            <MethodPath endpoint={endpoint} />
          </button>
        ) : (
          <span className="flex min-w-0 items-baseline gap-2" title={`${endpoint.method} ${endpoint.path}`}>
            <MethodPath endpoint={endpoint} />
          </span>
        )}
      </span>

      <span role="gridcell" data-testid="cell-total" className="num text-right tabular-nums">
        {endpoint.total === null ? (
          <Absent title={t('dashboard.row.noData')} />
        ) : (
          numberFormat.format(endpoint.total)
        )}
      </span>

      {STATUS_COLUMNS.map((bucket) => {
        const count = buckets[bucket]
        return (
          <span
            key={bucket}
            role="gridcell"
            data-testid={`cell-${bucket}`}
            title={t(COLUMN_DESCRIPTION_KEYS[bucket])}
            className={`num text-right tabular-nums ${count ? BUCKET_STYLES[COLUMN_BUCKET[bucket]].text : ''}`}
          >
            {count ? (
              numberFormat.format(count)
            ) : (
              // An em dash covers two different facts, and the tooltip is the
              // only thing that separates them: a measured window that simply
              // saw no 3xx, versus an endpoint we have no data for at all.
              <Absent
                title={t(measured ? 'dashboard.row.noRequestsInBucket' : 'dashboard.row.noData')}
              />
            )}
          </span>
        )
      })}

      <span role="gridcell" data-testid="cell-ratio">
        <StatusRatioBar byStatus={endpoint.by_status} total={endpoint.total} />
      </span>

      <span role="gridcell" data-testid="cell-trend" className="text-right">
        {!cliffApplies ? (
          <Absent title={t('dashboard.row.cliffNotApplicable')} />
        ) : !measured ? (
          // `detect_cliff` returns False whenever the 24h baseline is missing,
          // which it is for ~1,058 of the 1,062 endpoints. Reading that False
          // as "steady" would assert normal traffic against a baseline that
          // does not exist — the same null-as-zero the rest of this row avoids.
          <Absent title={t('dashboard.row.cliffNoBaseline')} />
        ) : endpoint.traffic_cliff ? (
          <span
            title={t('dashboard.row.cliffTitle')}
            className="rounded bg-degraded/15 px-1.5 py-0.5 text-xs font-medium text-degraded"
          >
            {t('dashboard.row.cliffBadge')}
          </span>
        ) : (
          <span className="text-xs text-muted">{t('dashboard.row.steady')}</span>
        )}
      </span>
    </div>
  )
}

function MethodPath({ endpoint }: { endpoint: EndpointSummary }) {
  return (
    <>
      <span className="num shrink-0 text-xs font-semibold text-muted">{endpoint.method}</span>
      <span className="num truncate text-fg">{endpoint.path}</span>
    </>
  )
}

/** An em dash, never a zero: the two mean different things in this table. */
function Absent({ title }: { title: string }) {
  return (
    <span title={title} className="text-muted">
      —
    </span>
  )
}
